import {Component} from 'preact';
import AddPaymentForm from '@components/account/Wallet/AddPaymentForm';
import PaymentSelector from '@components/cart/PaymentSelector';
import {Button, FormFeedback, Processing, TextField} from '@ultradent/components'
import {trimFormData} from '@ultradent/utilities/Strings';
import {client} from '@core/GraphQLProvider';
import {Query} from 'react-apollo';
import {Formik} from 'formik';
import {GetCustomer} from '@services/Customer/query.graphql';
import {$CONTENT_WINDOW} from '@config/selectors';
import prop from 'ramda/src/prop';
import invoke from 'lodash.invoke';
import reject from 'ramda/src/reject';
import not from 'ramda/src/not';
import ServiceError from '@components/common/ServiceError';
import useCheckoutApi from '@hooks/useCheckoutApi';

const withQuery = ( Component ) => {
    function Wrapper ( props ) {
        return (
            <Query client={client}
                   fetchPolicy="network-only"
                   query={GetCustomer}
                   variables={{
                       withCartSummary: true,
                       withAddresses: true,
                       withWallet: true,
                       withAccountDetail: true
                   }}>
                {PaymentSelectionQuery( props )}
            </Query>
        );
    }

    Wrapper.displayName = `withQuery(${Component.displayName || Component.name})`;

    return Wrapper;
};

const PaymentSelectionWithCheckoutApi = ( {customer, ...props} ) => {
    const checkoutApi = useCheckoutApi( customer );

    return <PaymentSelection
        account={customer.account}
        checkoutApi={checkoutApi}
        {...props}/>;
};

export const PaymentSelectionQuery = ( props = {} ) => {
    let retryAttempts = 0;

    return ( {data, loading, error, refetch} ) => {
        if ( loading ) {
            return <div className="flex justify-center">
                <Processing className="justify-center">Loading your payment options</Processing>
            </div>
        }
        if ( error || !data ) {
            if ( retryAttempts < 2 ) {
                retryAttempts++;
                refetch();
                return (
                    <div className="flex justify-center">
                        <Processing className="justify-center">Still loading your payment options</Processing>
                    </div>
                )
            }
            return <ServiceError message="Sorry, we're experiencing a problem and are unable to load your payment options."/>;
        }

        return <PaymentSelectionWithCheckoutApi
            customer={data.customer}
            refetchQuery={refetch}
            loading={loading}
            {...props} />;
    };
};

export class PaymentSelection extends Component {

    defaultProps = {
        addressBook: [],
        paymentOptions: []
    };

    constructor ( props ) {
        super( props );

        this.onPaymentAdded = this.onPaymentAdded.bind( this );
        this.handleSubmitPayment = this.handleSubmitPayment.bind( this );
    }

    scrollToTop () {
        var pane = document.querySelector( $CONTENT_WINDOW );
        if ( pane ) {
            pane.scrollTop = 0;
        }
        window.scrollTop = 0;
    }

    onPaymentAdded ( addedPayment ) {
        this.props.checkoutApi.onPaymentAdded( addedPayment );
        this.scrollToTop();
    }

    handleSubmitPayment ( values ) {
        /* note -> we send reference to the submit button due to a legacy implementation in checkout.js
         * the legacy Cart/Checkout module inspects the current label of the button which submitted the form
         * to determine if the "processing order" layer should be displayed
         */
        invoke( this.props, 'onSubmitPayment', {target: this.submitButton.base} );
    }

    render () {
        const {checkoutApi} = this.props;

        const initialValues = {
            PONumber: checkoutApi.PONumber,
            SelectedPaymentMethod: prop( 'id', checkoutApi.selectedPaymentOption ),
            SelectedBillingAddress: prop( 'id', checkoutApi.selectedBillingAddress )
        };

        // Add new credit card form
        if ( !checkoutApi.paymentOptions?.length ) {
            return (
                <AddPaymentForm
                    addressBook={checkoutApi.addressBook.getAddressesByType( 'Billing' )}
                    onAddressAdded={checkoutApi.onBillingAddressAdded}
                    onPaymentAdded={this.onPaymentAdded}/>
            );
        }

        // Select from existing payment methods
        return (
            <div>
                <PaymentSelector
                    selectedAddressOption={checkoutApi.selectedBillingAddress}
                    selectedPaymentOption={checkoutApi.selectedPaymentOption}
                    paymentOptions={checkoutApi.paymentOptions}
                    addressBook={checkoutApi.addressBook.getAddressesByType( 'Billing' )}
                    allowAddPaymentMethod={true}
                    onPaymentOptionChange={checkoutApi.onPaymentOptionChange}
                    onPaymentAdded={this.onPaymentAdded}
                    onAddressChange={checkoutApi.onBillingAddressChange}
                    onAddressAdded={checkoutApi.onBillingAddressAdded}
                />

                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validate={formData => {
                        const errors = {};
                        const trimmedFormData = trimFormData( formData );
                        const {SelectedPaymentMethod, SelectedBillingAddress, PONumber} = trimmedFormData;

                        if ( checkoutApi.isPORequired ) {
                            errors.PONumber = (!PONumber || PONumber === '') && {
                                short: 'This field is required',
                                long: 'Purchase order number is required'
                            };
                        }

                        errors.SelectedPaymentMethod =
                            !SelectedPaymentMethod && 'Please select or add a credit card';

                        if ( SelectedPaymentMethod &&
                            checkoutApi.selectedPaymentOption.paymentMethodType === 'terms' ) {
                            errors.SelectedBillingAddress =
                                !SelectedBillingAddress && 'Please select or add a billing address';
                        }

                        return reject( not, errors );
                    }}
                    onSubmit={values => this.handleSubmitPayment( trimFormData( values ) )}
                    render={formikBag => {

                        return <div className="my-12">
                            <TextField
                                label="Purchase order number"
                                labelAppend={checkoutApi.isPORequired ? '(required)' : '(optional)'}
                                id="PONumber"
                                type="text"
                                debounceOnChange={false}
                                onChange={e => {
                                    formikBag.handleChange( e );
                                    checkoutApi.onPONumberChange( e.target.value );
                                }}
                                value={formikBag.values.PONumber}
                                error={formikBag.errors.PONumber}
                                maxLength="50"/>


                            <FormFeedback className="bg-fail" {...formikBag}/>

                            <div className="my-8 flex justify-center">
                                <Button ref={ref => this.submitButton = ref}
                                        fill
                                        large
                                        type="submit"
                                        onClick={formikBag.handleSubmit}>
                                    Complete payment
                                </Button>
                            </div>
                        </div>
                    }}/>
            </div>
        );
    }
}

export default withQuery( PaymentSelection );
